<script lang="ts" setup>
    import { PayerMixReportDateType, ServiceLocationType, InsuranceType } from '../../Enums/ChoiceEnums';
    import { postJson } from '../../lib/ChoiceApi';
    import { ref, onMounted, computed, nextTick, onErrorCaptured, onBeforeMount } from 'vue';
    import * as _ from 'lodash'
    import LoadingSection from '../../components/LoadingSection.vue';
    import ChoiceTable from '../../components/ChoiceTable.vue';
    import { ChoiceApi } from '../../LibMain';
    import { IPayerFinancialClassPickerInfoModel, IProcedureCodePickerInfoModel, IPayerPickerInfoModel, IRenderingProviderPickerInfoModel, IServiceLocationInfoModel } from '../../lib/Models/OptionValuesModels';
    import PrettyDate from '../../components/PrettyDate.vue';
    import * as StringFormat from '../../lib/StringFormat';
    import { TableRowType } from '../../Enums/UiEnums';
    import MessageBadge from '../../components/Messages/MessageBadge.vue';
    import { ICol, FormattedValueStr, RenderModeType, BodyValueStrDelegate, ITableColumns } from '../../lib/ChoiceTableHelpers';
    import { ElapsedTime } from '../../lib/ElapsedTime';
    import FoldingPanel from '../../components/FoldingPanel.vue';
    import SelectAndOrderBy from '../../components/SelectAndOrderBy.vue'
    import OptionsDropPanel from '../../components/OptionsDropPanel.vue'
    import SelectionCount from '../../components/SelectionCount.vue';
    import ToggleIcon from '../../components/ToggleIcon.vue';
    import * as DateUtils from '../../lib/DateUtils';
    import PayerMixQueryOptions, { IPayerMixQueryData } from './PayerMixQueryOptions.vue';
    import * as Export from '../../lib/Export';
    import { ToFilenameTimestamp } from '../../lib/StringFormat';
    import { PayerMixDateTypes, PayerMixInsuranceTypes} from './PayerMixUtils'
    import FoldingFieldset from '../../components/FoldingFieldset.vue';
    import DropDownSplitButton from '../../components/DropDownSplitButton.vue'
    import DropDownButtonItem from '../../components/DropDownButtonItem.vue'
    import DropDownButtonDivider from '../../components/DropDownButtonDivider.vue'

    interface IReportTotals{
        ClaimsCnt: number, 
        ChargesAmt: number, 
        PaymentsAmt: number, 
        AdjustmentAmt: number
    }

    interface ISelection {
        selected?:boolean
    }

    interface IEntityId {
        entity_id: number
    }

    interface IProcedurePseudoId {
        procedurePseudoId?: number
    }

    enum IterateeTypes {
        finClassId = 'finClassId',
        payeeEid = 'payerEid',
        renderingProvEid = 'renderingProvEid',
        locationEid = 'locationEid',
        procedurePseudoId = 'procedurePseudoId',
    }

    // Date returned from the API
    interface IClaimProcedureItemModel
    {
            diagnosis_id: number,
            claim_id: number,
            
            units: number,      // Units of exepenable item used

            procCode?: string,  // Procedure Code

            // Amounts
            diagAmt?: number,       // Diagnosis
            allowedAmt?: number,
            paidAmt?: number,
            adjustmentAmt?: number,

            finClassId?: number,    // Financial Class

            payerEid?: number,          // Payer entity ID
            renderingProvEid?: number,  // Rendering Provider entity ID
            locationEid?: number        // Service Location entity ID
    }

    // Add a PseudoId for procedure codes
    type IReportClaimProcedureItemModel = (IClaimProcedureItemModel & IProcedurePseudoId);

    // Reactive data
    const rawReportData = ref<(IReportClaimProcedureItemModel)[]>();
    const loadingDataMessage = ref<string>();
    const loadingApiData = ref(false);

    const queryDataPanelOpen = ref(true);
    const aggregatedData = ref<IGroup[]>([]);
    const exportingToExcel = ref(false);

    // Performance monitors
    const showPerformanceData = ref(false);
    const ellapsedTimeAggregate = ref(0);
    const ellapsedTimeReportDataApiCall = ref(0);
    const ellapsedTimeOptions = ref(0);

    // Options data
    const FinancialClassPickerInfo = ref<(IPayerFinancialClassPickerInfoModel & ISelection)[]>([]);
    const PayerPickerInfo = ref<(IPayerPickerInfoModel & ISelection)[]>([]);
    const RenderingProviderPickerInfo = ref<(IRenderingProviderPickerInfoModel & ISelection)[]>([]);
    const ServiceLocationPickerInfo = ref<(IServiceLocationInfoModel & ISelection)[]>([]);
    const ProcedureCodePickerInfo = ref<(IProcedureCodePickerInfoModel & ISelection & IProcedurePseudoId)[]>([]);

    // Query values
    const queryValues = ref<IPayerMixQueryData>({
        dateType: PayerMixReportDateType.ServiceDate,
        startDate: DateUtils.toNoTzDate(DateUtils.addMonth(new Date(), -1)),
        endDate: DateUtils.toNoTzDate(DateUtils.endOfDay()),
        includeProcedureCode: false,
        includeAllowedAmount: false,
        insuranceType: InsuranceType.Primary
    });    

    const ReportLevelOptions = ref([
        {name: "Financial Class", selected: true, order: 1, data: {Iteratee : IterateeTypes.finClassId }},
        {name: "Provider", selected: false, order: 1, data: {Iteratee : IterateeTypes.renderingProvEid }},
        {name: "Service Location", selected: false, order: 1, data: {Iteratee : IterateeTypes.locationEid }},
        {name: "Payer", selected: true, order: 1, data: {Iteratee : IterateeTypes.payeeEid }},
        {name: "Procedure Code", selected: false, order: 1, data: {Iteratee : IterateeTypes.procedurePseudoId, ShowName: false }, ProcedureCodeColumn: true, TotalUnitsColumn: true},
        {name: "Procedure Code & Name", selected: false, order: 1, data: {Iteratee : IterateeTypes.procedurePseudoId, ShowName: true }, ProcedureCodeColumn: true, ProcedureCodeNameColumn: true, TotalUnitsColumn: true}
    ]);

    const VisibleReportLevelOptions = computed(() => 
        ReportLevelOptions.value.filter(option => 
        {
            if (option.data.Iteratee === IterateeTypes.procedurePseudoId)
                return queryValues.value.includeProcedureCode;
            return true;
        }));


    const SelectedReportLevels = computed(() => {
        return VisibleReportLevelOptions.value.filter(o => o.selected).map(o => o.data.Iteratee);
    });

    // Fetch options data from API
    function GetPickerOptionsFromApi() {

        const finClassPromise = ChoiceApi.optionValues.PayerFinancialClassPickerInfo(true)
            .then(resp => {
                FinancialClassPickerInfo.value = resp;
                FinancialClassPickerInfo.value.forEach(o => o.selected = true);
            });

        const providerPromise = ChoiceApi.optionValues.RenderingProviderPickerInfo().then(resp => {
            RenderingProviderPickerInfo.value = resp
            RenderingProviderPickerInfo.value.forEach(o => { 
                o.selected = true;

                const maxNameLen = 75;
                if (o.name.length > maxNameLen)
                    o.name = `${o.name.substring(0, maxNameLen)}...`;
            });
        });

        const payerPromise = ChoiceApi.optionValues.PayerPickerInfo().then(resp => {
            PayerPickerInfo.value = resp;
            PayerPickerInfo.value.forEach(o => o.selected = true);
        });

        const serviceLocationPromise = ChoiceApi.optionValues.ServiceLocationPickerInfo().then(resp => 
        {
            ServiceLocationPickerInfo.value = resp
            ServiceLocationPickerInfo.value.forEach(o => o.selected = true);
        });

        const procedureCodePromise = ChoiceApi.optionValues.ProcedureCodePickerInfo().then(resp =>{
            ProcedureCodePickerInfo.value = resp;
            ProcedureCodePickerInfo.value.forEach(o => o.selected = true);
        });


        return Promise.all([finClassPromise, providerPromise, payerPromise, serviceLocationPromise, procedureCodePromise]);
    }

    // Get the raw report data from the API
    async function GetRawReportDataFromApi() {
        // Clear the pseudo procedure IDs
        ProcedureCodePickerInfo.value.forEach(o => o.procedurePseudoId = undefined);

        let apiPropValues = {
            includeProcedureCode: queryValues.value.includeProcedureCode,
            includeAllowedAmount: queryValues.value.includeAllowedAmount,
            insuranceType: queryValues.value.insuranceType,
            dateType: queryValues.value.dateType,
            startDate: queryValues.value.startDate,
            endDate: queryValues.value.endDate
        };

        const monitor = new ElapsedTime();
        rawReportData.value = await postJson("api/Reports/PayerMix/ClaimProcedureItems", apiPropValues, (httpResp) => {alert(`Error getting report data: ${httpResp}`)});

        // Set the pseudo procedure IDs
        if (rawReportData.value && queryValues.value.includeProcedureCode)
        {
            // Create an ID for each procedure code in the data
            const procCodesIDDict = GetProcCodePseudoIdDict();

            // Update the procedure codes with the associated ID
            for(let procCodeInfo of ProcedureCodePickerInfo.value)
                procCodeInfo.procedurePseudoId = procCodesIDDict[ProcCodeKey(procCodeInfo.procCode)];

            // Update the report data with the procedure codes ID
            for (const rptItem of rawReportData.value)
                rptItem.procedurePseudoId = procCodesIDDict[ProcCodeKey(rptItem.procCode)];
        }
        ellapsedTimeReportDataApiCall.value = monitor.MilliSeconds;

        queryDataPanelOpen.value = false;
    }


    // Get a dictionary of procedure codes and generate their pseudo ID
    function GetProcCodePseudoIdDict(): Record<string, number>
    {
        if (!rawReportData.value)
            return {};

        // Create an ID for each procedure code in the data
        const uniqueProcCodes = _.uniq(rawReportData.value.map(o => ProcCodeKey(o.procCode))).sort();
        const procCodeDict: Record<string, number> = {};
        for(let i=0; i < uniqueProcCodes.length; ++i)
            procCodeDict[uniqueProcCodes[i]] = i;
        return procCodeDict;
    }

    // Get a valid procedure code key
    function ProcCodeKey(procCode: string | undefined | null)
    {
        if (!procCode)
            return "__NoProcCode__";
        return procCode;
    }

    onBeforeMount(() =>{
        // Set values used for development
        if ("localhost" === location.host)
        {
            queryValues.value.includeProcedureCode = true;
            queryValues.value.startDate = DateUtils.toNoTzDate(new Date(2000, 1, 1));
            showPerformanceData.value = true;
        }
    });

    onMounted(async () => {
        const monitorOptionsCallTime = new ElapsedTime();
        await GetPickerOptionsFromApi();
        ellapsedTimeOptions.value = monitorOptionsCallTime.MilliSeconds;
    });

    onErrorCaptured(err => {
        alert(err.message);
    });

    // Call the API and then update the report table
    async function RunQuery()
    {
        rawReportData.value = undefined; 

        loadingApiData.value = true;
        loadingDataMessage.value = "Loading Report Data...";
        await GetRawReportDataFromApi();

        loadingDataMessage.value = "Formatting Report Data";

        InitializedReportTableData();
        loadingDataMessage.value = "";
        loadingApiData.value = false;

        queryDataPanelOpen.value = false;
    }

    async function RunQueryForCurrentMonth()
    {
        queryValues.value.startDate = DateUtils.toNoTzDate(DateUtils.startOfMonth());
        queryValues.value.endDate = DateUtils.toNoTzDate(DateUtils.endOfDay());
        RunQuery();
    }

    async function RunQueryForLastMonth()
    {
        const lastMonthRange = DateUtils.lastMonthRange();
        queryValues.value.startDate = DateUtils.toNoTzDate(lastMonthRange.startDate);
        queryValues.value.endDate = DateUtils.toNoTzDate(lastMonthRange.endDate);
        RunQuery();
    }

    async function RunQueryForLastQuarter()
    {
        const lastQuarterRange = DateUtils.lastQuarterRange();
        queryValues.value.startDate = DateUtils.toNoTzDate(lastQuarterRange.startDate);
        queryValues.value.endDate = DateUtils.toNoTzDate(lastQuarterRange.endDate);
        RunQuery();
    }


    // Get the intial report data
    function InitializedReportTableData()
    {
        aggregatedData.value = Aggregate(_.first(SelectedReportLevels.value) ?? IterateeTypes.finClassId);
    }

    let forceRecomputeFlag_filteredRawReportData = ref(false);

    // Filter the raw data based on user filters
    const filteredRawReportData = computed(() =>{

        // Is there anything to filter?
        let filteredData = rawReportData.value;
        if (undefined === filteredData)
            return;

        // Covers both payers & financial class
        if (SelectedPayeeIDs.value.length !== PayerPickerInfoFilterOptions.value.length)
            filteredData = FilterReportData(SelectedPayeeIDs.value, filteredData, (claim) => claim.payerEid);

        if (SelectedProviderIDs.value.length !== RenderingProvidersFilterOptions.value.length)
            filteredData = FilterReportData(SelectedProviderIDs.value, filteredData, (claim) => claim.renderingProvEid);

        if (SelectedLocationIDs.value.length !== ServiceLocationFilterOptions.value.length)
            filteredData = FilterReportData(SelectedLocationIDs.value, filteredData, (claim) => claim.locationEid);

        if (SelectedProcedureIDs.value.length !== ProcedureCodesFilterOptions.value.length)
            filteredData = FilterReportData(SelectedProcedureIDs.value, filteredData, (claim) => claim.procedurePseudoId);

        // Clear the forcing flag
        forceRecomputeFlag_filteredRawReportData.value = false;

        // Force new aggregation
        InitializedReportTableData();
        return filteredData;
    });


    // Filter the provided data
    function FilterReportData(selectedValues: any[] , dataToFilter: IReportClaimProcedureItemModel[], fieldToFilterBy: DataValueDelegate){
        return dataToFilter.filter(dataItem => selectedValues.includes(fieldToFilterBy(dataItem)));
    }
    type DataValueDelegate = (claim: IReportClaimProcedureItemModel) => any;

    interface IGroup
    {
        Name: string,
        ShowChild: boolean,
        ID: number,
        IDType: IterateeTypes,
        count: number,

        ClaimCount?: number,
        PercentOfClaims?: number,
        AverageCharge?: number,
        TotalCharges?: number,
        PercentOfTotalCharges?: number,
        AveragePayment?: number,
        TotalPayments?: number,
        PercentOfTotalPayments?: number,
        AverageAdjustments?: number,
        TotalAdjustments?: number,
        PercentOfTotalAdjustments?: number,
        AverageAllowed?: number,    // Only computed when exporting and we have allowed amount data
        TotalAllowed?: number,      // Only computed when exporting and we have allowed amount data

        ParentGroup?: IGroup
        Level: number,
        ChildGroups?: IGroup[],

        ExtraData: {
            totalUnits?: number     // Only added to the procedure type rows
        }
    }

    const ReportTotals = computed<{ClaimsCnt: number, ChargesAmt: number, PaymentsAmt: number, AdjustmentAmt: number }>(() =>{
        const topLevelData = flattenedAggregatedReportData.value.filter(data => 0 === data.Level);
        return {
            ClaimsCnt: _.sumBy(topLevelData, val => val.ClaimCount ?? 0),
            ChargesAmt: _.sumBy(topLevelData, val => val.TotalCharges ?? 0),
            PaymentsAmt: _.sumBy(topLevelData, val => val.TotalPayments ?? 0),
            AdjustmentAmt: _.sumBy(topLevelData, val => val.TotalAdjustments ?? 0)
        }
    });

    // Aggregate a group to get totals
    function Aggregate(iteratee: IterateeTypes, parentGroup?: IGroup) : Array<IGroup> {
        const monitorTime = new ElapsedTime();

        // Get the raw data at this level
        const parentGroupData = GetFilteredRawReportDataForGroup(parentGroup);

        const groups = _.groupBy(parentGroupData, iteratee);

        const groupArray: Array<IGroup> = [];
        for(const [keyVal, grpValues] of Object.entries(groups))
        {
            if (!grpValues)
                continue;

            const grpClaimCount = _.uniqBy(grpValues, val => val.claim_id).length;
            const grpTotalChargesAmt = _.sumBy(grpValues, item => item.diagAmt ?? 0);
            const grpPaymentsAmt = _.sumBy(grpValues, item => item.paidAmt ?? 0);
            const grpAdjustmentAmt = _.sumBy(grpValues, item => item.adjustmentAmt ?? 0);

            const newItem = {
                Name: IdToName(keyVal, iteratee),
                ShowChild: false,
                ID: +keyVal,
                IDType: iteratee,
                count: grpValues.length,
                ClaimCount: grpClaimCount,
                PercentOfClaims: CalculatePercentage(grpClaimCount, ReportTotals.value.ClaimsCnt),
                AverageCharge: CalculateAverage(grpTotalChargesAmt, grpClaimCount),
                TotalCharges: grpTotalChargesAmt,
                PercentOfTotalCharges: CalculatePercentage(grpTotalChargesAmt, ReportTotals.value.ChargesAmt),
                AveragePayment: CalculateAverage(grpPaymentsAmt, grpClaimCount),
                TotalPayments: grpPaymentsAmt,
                PercentOfTotalPayments: CalculatePercentage(grpPaymentsAmt, ReportTotals.value.PaymentsAmt),
                AverageAdjustments: CalculateAverage(grpAdjustmentAmt, grpClaimCount),
                TotalAdjustments: grpAdjustmentAmt,
                PercentOfTotalAdjustments: CalculatePercentage(grpAdjustmentAmt, ReportTotals.value.AdjustmentAmt),
                ParentGroup: parentGroup,
                Level: parentGroup? 1 + parentGroup?.Level : 0,
                ExtraData: {}
            } as IGroup;

            // Get total procedure units?
            newItem.ExtraData.totalUnits = _.sumBy(grpValues, "units");

            // Generate allowed amount values?
            if (computeAllowedAmountValues.value)
            {
                newItem.TotalAllowed = _.sumBy(grpValues, item => item.allowedAmt ?? 0);
                newItem.AverageAllowed = CalculateAverage(newItem.TotalAllowed, grpClaimCount);
            }

            groupArray.push(newItem);

        }

        ellapsedTimeAggregate.value = monitorTime.MilliSeconds;

        return _.orderBy(groupArray, grp => grp.Name);
    }

    // Get the raw data based on the group provided
    function GetFilteredRawReportDataForGroup(group: IGroup | undefined): IReportClaimProcedureItemModel[]
    {
        if (!group)
            return filteredRawReportData.value ?? [];
        
        let parentGroupData = filteredRawReportData.value;

        // Filter down to the current grouping
        let parent = group;
        while (parent)
        {
            switch(parent.IDType)
            {
                case IterateeTypes.finClassId:
                parentGroupData = parentGroupData?.filter(o => o.finClassId === parent!.ID);
                break;

                case IterateeTypes.locationEid:
                parentGroupData = parentGroupData?.filter(o => o.locationEid === parent!.ID);
                break;

                case IterateeTypes.payeeEid:
                parentGroupData = parentGroupData?.filter(o => o.payerEid === parent!.ID);
                break;

                case IterateeTypes.renderingProvEid:
                parentGroupData = parentGroupData?.filter(o => o.renderingProvEid === parent!.ID);
                break;
            }
            if (!parent.ParentGroup)
                break;
            parent = parent.ParentGroup;
        }
        return parentGroupData ?? [];
    }

    // Protect us from div-by-zero
    function CalculateAverage(sumOfTerms: number | undefined | null, countOfTerms: number | undefined | null) : number | undefined {
        if (0 === countOfTerms)
            return 0;
        if (!countOfTerms || !sumOfTerms)
            return;

        return sumOfTerms/countOfTerms;
    }

    function CalculatePercentage(numerator: number | undefined | null, denominator: number | undefined | null) : number | undefined {
        if (0 === numerator)
            return 0;
        if (!numerator || !denominator)
            return;

        return 100*numerator/denominator;
    }

    const flattenedAggregatedReportData = computed(()=>{ 
        return flattenAggregatedData(aggregatedData.value) });

    function flattenAggregatedData(groups: IGroup[] | undefined):IGroup[] {
        let reportData = [];
        if (!groups)
            return [];

        const iterateeTypeDict : Record<IterateeTypes, number[]>= {
            [IterateeTypes.finClassId]: SelectedFinancialClassIDs.value,
            [IterateeTypes.payeeEid]: SelectedPayeeIDs.value,
            [IterateeTypes.renderingProvEid]: SelectedProviderIDs.value,
            [IterateeTypes.locationEid]: SelectedLocationIDs.value,
            [IterateeTypes.procedurePseudoId]: SelectedProcedureIDs.value
        };

        for(const grp of groups)
        {
            // Get the IDs for this group type
            const constIDs = iterateeTypeDict[grp.IDType];
            if (!constIDs)
            {
                alert(`Unhandled type: ${grp.IDType}`);
                continue;
            }

            // Is the ID selected?
            if (undefined === constIDs.find(id => id == grp.ID))
                continue;

            reportData.push(grp);

            if (grp.ChildGroups && grp.ShowChild)
            {
                for(const childGroup of flattenAggregatedData(grp.ChildGroups))
                    reportData.push(childGroup);
            }
        }

        return reportData;
    }

    const computeAllowedAmountValues = computed(()=>
        queryValues.value.includeAllowedAmount && exportingToExcel.value
    );

    // The results table columns
    const resultsColumns = computed<ITableColumns>(() => {
        let columns: ITableColumns = [];
        columns.push({ Name: '', ExtraData: { FoldingCell: true }}); // Folding column

        for(const level of SelectedReportLevels.value)
        {
            if (level === IterateeTypes.finClassId)
                columns.push({ Name: 'Financial Class', DataField: 'Name', ExtraData:{Iteratee : level }, FormatBodyValue: GetLevelName });
            if(level === IterateeTypes.payeeEid)
                columns.push({ Name: 'Payer', DataField: 'Name', ExtraData:{Iteratee : level }, FormatBodyValue: GetLevelName });
            if(level === IterateeTypes.renderingProvEid)
                columns.push({ Name: 'Provider', DataField: 'Name', ExtraData:{Iteratee : level }, FormatBodyValue: GetLevelName });
            if(level === IterateeTypes.locationEid)
                columns.push({ Name: 'Service Location', DataField: 'Name', ExtraData:{Iteratee : level }, FormatBodyValue: GetLevelName });
        }

        // See if any Procedure Code related columns are selected
        if (ReportLevelOptions.value.find(o => o.ProcedureCodeColumn && o.selected))
            columns.push({ Name: 'Procedure Code', DataField: 'procCode', FormatBodyValue: FormatProcedureCode });
        if (ReportLevelOptions.value.find(o => o.ProcedureCodeNameColumn && o.selected))
            columns.push({ Name: 'Procedure Name', FormatBodyValue: FormatProcedureNameValue });
        if (ReportLevelOptions.value.find(o => o.TotalUnitsColumn && o.selected))
            columns.push({ Name: 'Total Procedure Units', FormatBodyValue: FormatTotalProcedureUnits });

        // See if (during export) we should include the allowed amount??
        if (computeAllowedAmountValues.value)
        {
            columns.push({ Name: 'Average Allowed', DataField: 'AverageAllowed', RenderMode: RenderModeType.AccountingMoney });
            columns.push({ Name: 'Total Allowed', DataField: 'TotalAllowed', RenderMode: RenderModeType.AccountingMoney });
        }


        // Add the rest of the columns
        columns = columns.concat(
        [
            { Name: "Claim Count", DataField: "ClaimCount", RenderMode: RenderModeType.PrettyNumber },
            { Name: "% of Claims", DataField: "PercentOfClaims", RenderMode: RenderModeType.Percent },
            { Name: "Average Charges", DataField: "AverageCharge", RenderMode: RenderModeType.AccountingMoney },
            { Name: "Total Charges", DataField: "TotalCharges", RenderMode: RenderModeType.AccountingMoney },
            { Name: "% of Total Charges", DataField: "PercentOfTotalCharges", RenderMode: RenderModeType.Percent },
            { Name: "Average Payment", DataField: "AveragePayment", RenderMode: RenderModeType.AccountingMoney },
            { Name: "Total Payments", DataField: "TotalPayments", RenderMode: RenderModeType.AccountingMoney },
            { Name: "% of Payments", DataField: "PercentOfTotalPayments", RenderMode: RenderModeType.Percent },
            { Name: "Average Adjustments", DataField: "AverageAdjustments", RenderMode: RenderModeType.AccountingMoney },
            { Name: "Total Adjustments", DataField: "TotalAdjustments", RenderMode: RenderModeType.AccountingMoney },
            { Name: "% of Adjustments", DataField: "PercentOfTotalAdjustments", RenderMode: RenderModeType.Percent }
        ]);
        return columns;
    });

    // Get the procedure code description for the "Procedure Name" column
    const FormatProcedureNameValue: BodyValueStrDelegate = (_value: any, _col: ICol, row: Record<string, any>) =>
    { 
        if (IterateeTypes.procedurePseudoId === row?.IDType)
            return ProcedureCodePickerInfo.value.find(o => o.procedurePseudoId === row.ID)?.description;
    }

    // Get the procedure code for the "Procedure Code" column
    const FormatProcedureCode: BodyValueStrDelegate = (_value: any, _col: ICol, row: Record<string, any>) =>
    { 
        if (IterateeTypes.procedurePseudoId === row?.IDType)
            return ProcedureCodePickerInfo.value.find(o => o.procedurePseudoId === row.ID)?.procCode;
    }

    const FormatTotalProcedureUnits: BodyValueStrDelegate = (_value: any, _col: ICol, row: Record<string, any>) => {
        return StringFormat.ToPrettyNumber(row.ExtraData?.totalUnits);
    }

    // Convert a id/type to an associated name
    function IdToName(id: number | string, iteratee: IterateeTypes): string {
        switch(iteratee)
        {
            case IterateeTypes.finClassId:
                return FinancialClassPickerInfo.value.find(item => item.financial_class_id == id)?.name ?? `(finClassId = ${id})`;

            case IterateeTypes.payeeEid:
                const payee = PayerPickerInfo.value.find(item => item.entity_id == id);
                if (payee)
                    return `${payee.entity_id} ${payee.name}`;
            break;

            case IterateeTypes.renderingProvEid:
                const provider = RenderingProviderPickerInfo.value?.find(item => item.entity_id== id);
                if (provider)
                    return provider.name;
            break;

            case IterateeTypes.locationEid:
                const location = ServiceLocationPickerInfo.value?.find(item => item.entity_id == id)
                if(location)
                    return location.name;
            break;

            case IterateeTypes.procedurePseudoId:
                const procCode = ProcedureCodePickerInfo.value?.find(item => item.procedurePseudoId == id)
                if(procCode)
                    return procCode.procCode;
            break;

            default:
                alert("Unexpected iteratee type: " + iteratee);
                break;
        }
        return `(${iteratee ?? "???"} = ${id})`;      
    }

    // Values to display in the results table footer
    const ResultsFooterValues = computed<Record<string, any>>(() =>{
        return {
            ClaimCount: ReportTotals.value.ClaimsCnt,
            TotalCharges: ReportTotals.value.ChargesAmt,
            TotalPayments: ReportTotals.value.PaymentsAmt,
            TotalAdjustments: ReportTotals.value.AdjustmentAmt
        };
    });

    function ToggleRow(grp: IGroup | any)
    {
        if (!flattenedAggregatedReportData.value)
            return;

        let localAggregatedData = aggregatedData.value;
        const aggregateGroup = GetAggregatedDataGroupItem(grp);
        if (!aggregateGroup)
            return;

        aggregateGroup.ShowChild = !aggregateGroup.ShowChild;

        // Compute the next level down?
        if (aggregateGroup.ShowChild)
        {
            if (!aggregateGroup.ChildGroups)
            {
                if (grp.Level < SelectedReportLevels.value.length)
                {
                    const ittr = SelectedReportLevels.value[grp.Level+1];
                    aggregateGroup.ChildGroups = Aggregate(ittr, grp);

                    // Update the data to trigger re-compute/re-draw
                    aggregatedData.value = localAggregatedData;
                }
            }
        }
    }

    // Get the IGroup item based on 
    function GetAggregatedDataGroupItem(grpToMatch: {ID: number, IDType: IterateeTypes}, parentGroups?: IGroup[]) : IGroup | undefined
    {
        if (!parentGroups)
            parentGroups = aggregatedData.value;

        for(const grp of parentGroups)
        {
            if (grp.ID == grpToMatch.ID && grp.IDType == grpToMatch.IDType)
                return grp;

            if (grp.ChildGroups)
            {
                const lowerGrp = GetAggregatedDataGroupItem(grpToMatch, grp.ChildGroups);
                if (lowerGrp)
                    return lowerGrp;
            }
        }
    }

    // Display the level name there the interatee matchs on the row and column
    const GetLevelName: BodyValueStrDelegate = (_value: any, col: ICol, row: Record<string, any>) => {
        if (col.ExtraData.Iteratee === row.IDType)
            return row.Name;

        if (!exportingToExcel.value)
            return;

        // Printing so look at parent levels for name
        let parent = row.ParentGroup;
        while(parent)
        {
            if (col.ExtraData.Iteratee  === parent.IDType)
                return parent.Name;
            parent = row.ParentGroup;
        }
    }

    const SelectedFinancialClassIDs = computed(()=> GetSelectedIDs(FinancialClassPickerInfo.value, "financial_class_id") );
    const SelectedFinancialClassesWithPayeeIDs = computed(()=> GetSelectedIDs(FinancialClassPickerInfoWithPayees.value, "financial_class_id") );

    // The payee and the financial class both need to be checked
    const SelectedPayeeIDs = computed(()=> {
        const selections = PayerPickerInfoFilterOptions.value
                .filter(o => o.selected && 
                        SelectedFinancialClassIDs.value.includes(o.financial_class_id) )
                .map(o => o.entity_id);  
        TriggerForceRecompute_FilteredRawReportData();
        return selections;
    });

    const SelectedProviderIDs = computed(() => GetSelectedIDs(RenderingProvidersFilterOptions.value));
    const SelectedLocationIDs = computed(() => GetSelectedIDs(ServiceLocationFilterOptions.value));
    const SelectedProcedureIDs = computed(() => GetSelectedIDs(ProcedureCodesFilterOptions.value, "procedurePseudoId"));

    // This method forces a recompute -- Vue isn't smart enough to always figure when to recompute data
    function TriggerForceRecompute_FilteredRawReportData()
    {
        nextTick(() => 
        {
            forceRecomputeFlag_filteredRawReportData.value = true;
            InitializedReportTableData();
        });
    }

    function GetSelectedIDs(collectionWithSelection : any[], idPropertyName: string = "entity_id"): number[]
    {
        const selections = collectionWithSelection
                .filter(o => o.selected)
                .map(o => o[idPropertyName]);
        TriggerForceRecompute_FilteredRawReportData();                
        return selections;
    }

    const FinClassFromID: BodyValueStrDelegate = (finClassIdValue: number|undefined): string|undefined => {
        return FinancialClassPickerInfo.value.find(finClass => finClass.financial_class_id == finClassIdValue)?.name;
    }

    // Convert a service location type enum value to a string
    const ToServiceLocationName: BodyValueStrDelegate = (value: any, _col: ICol, _row: Record<string, any> | undefined): string | undefined | null => {
        switch(value)
        {
            case ServiceLocationType.Facility:
                return "Facility";
            case ServiceLocationType.IndependentLab:
                return "IndependentLab";
            default:
                return `Unknown ServiceLocationType value: ${value}`;
        }
    }

    const financialClasspayeeTableColumns: ITableColumns = [
        { Name: "", DataField: "selected", RenderMode: RenderModeType.Selection },
        { Name: "Name", DataField: "name"}
    ];

    const payeeTableColumns: ITableColumns = [
        //{ Name: "", ExtraData:{selection:true}},
        { Name: "", DataField: "selected", RenderMode: RenderModeType.Selection },
        { Name: "Financial Class", DataField: "financial_class_id", FormatBodyValue: FinClassFromID },
        { Name: "Name", DataField: "name" },
        { Name: "ID", DataField: "entity_id" },
        { Name: "Address", DataField: "address" },
        { Name: "City", DataField: "city" },
        { Name: "Zip", DataField: "zip" },
        { Name: "Phone", DataField: "phone"},
    ];

    const renderingProviderTableColumns: ITableColumns=[
        { Name: "", DataField: "selected", RenderMode: RenderModeType.Selection },
        { Name: "Name", DataField: "name"},
        { Name: "NPI", DataField: "national_provider_id" },
        { Name: "Group", DataField: "group_name" }
    ];

    const serviceLocationTableColumns: ITableColumns = [
        { Name: "", DataField: "selected", RenderMode: RenderModeType.Selection },
        { Name: "Name", DataField: "name"},
        { Name: "Address", DataField: "address"},
        { Name: "City", DataField: "city"},
        { Name: "Zip", DataField: "zip_code"},
        { Name: "POS", DataField: "pos"},
        { Name: "Location Type", FormatBodyValue: ToServiceLocationName},
    ];

    const procedureCodesTableColumns: ITableColumns = [
        { Name: "", DataField: "selected", RenderMode: RenderModeType.Selection },
        { Name: "Code", DataField: "procCode"},
        { Name: "Description", DataField: "description"},
        { Name: "Type", DataField: "code_type"},
    ];

    // Get the Financial Classes with at least one payee
    const FinancialClassPickerInfoWithPayees = computed(() => {
        return FinancialClassPickerInfo.value.filter(fc => PayerPickerInfo.value.find(p => p.financial_class_id == fc.financial_class_id));
    });

    // Get the payees represented in the raw report data
    const PayerPickerInfoFilterOptions = computed(() =>{
        const payeeEidsInData = _.uniq(rawReportData.value?.map(o => o.payerEid));

        // Must be in the raw data and the fin class must be selected
        const payeesToDisplay = PayerPickerInfo.value.filter(payee => payeeEidsInData?.includes(payee.entity_id) 
                            && SelectedFinancialClassIDs.value.includes(payee.financial_class_id));
        return _.sortBy(payeesToDisplay, payee => payee.name);
    });

    // The providers represented in the raw report data
    const RenderingProvidersFilterOptions = computed(()=>{
        const renderingProviderEidsInData = _.uniq(rawReportData.value?.map(o => o.renderingProvEid));
        return RenderingProviderPickerInfo.value.filter(provider => renderingProviderEidsInData?.includes(provider.entity_id) );
    });

    // The locations represented in the raw report data
    const ServiceLocationFilterOptions = computed(()=>{
        const locationIds = _.uniq(rawReportData.value?.map(o => o.locationEid));
        return ServiceLocationPickerInfo.value.filter(location => locationIds?.includes(location.entity_id));
    });

    // Procedure codes represented in the raw report data
    const ProcedureCodesFilterOptions = computed(()=>{
        const procCodes = _.uniq(rawReportData.value?.map(o => o.procCode));
        return ProcedureCodePickerInfo.value.filter(procCodeInfo => procCodes?.includes(procCodeInfo.procCode));
    });

    function PrintReport(){
        window.print();
    }

    function ExportToExcel(): void {
        exportingToExcel.value = true;

        const columnsToExport = resultsColumns.value.filter(col => col.RenderMode != RenderModeType.Selection && col.DataField);

        // Ignore the selection column
        const headerNames = columnsToExport.map(col => col.Name);

        // Generate a title section
        let exportData = [];
        exportData.push(["Payer Mix Report"]);
        exportData.push([`Report Date: ${DateUtils.prettyDate(new Date())}, ${DateUtils.prettyTime(new Date())}`]);
        exportData.push([`Date Range: ${DateUtils.prettyDate(queryValues.value.startDate) } to ${DateUtils.prettyDate(queryValues.value.endDate)}`]);
        exportData.push([`Date Type: ${reportDateTypeName.value}, Insurance Type: ${reportInsuranceTypeName.value}`]);
        exportData.push([]);

        const mergeHeaderCells = [
            Export.CellRange(new Export.CellModel(0, 0), new Export.CellModel(0, headerNames.length)),
            Export.CellRange(new Export.CellModel(1, 0), new Export.CellModel(1, headerNames.length)),
            Export.CellRange(new Export.CellModel(2, 0), new Export.CellModel(2, headerNames.length)),
            Export.CellRange(new Export.CellModel(3, 0), new Export.CellModel(3, headerNames.length)),
        ];
        exportData.push(headerNames);

        let reportDataGroups = Aggregate(_.first(SelectedReportLevels.value) ?? IterateeTypes.finClassId);
        for (const rootLevelGroup of reportDataGroups)
        {
            ExpandAggregateGroupItems(rootLevelGroup)
        }

        const flattenedData = flattenAggregatedData(reportDataGroups);

        // Export the table body
        for(const row of flattenedData)
        {
            let exportRow = [];
            for(const col of columnsToExport)
            {
                const formattedValue = FormattedValueStr(col, row, TableRowType.Body);
                exportRow.push(formattedValue);
            }
            exportData.push(exportRow);
        }

        // Add table footer
        let footerRow = [];
        for(const col of columnsToExport)
        {
            const formattedValue = FormattedValueStr(col, ResultsFooterValues.value, TableRowType.Footer);
            footerRow.push(formattedValue);
        }
        exportData.push(footerRow);


        exportingToExcel.value = false;
        Export.ExportSheetToExcel(exportData, `PayerMix_${ToFilenameTimestamp()}.xlsx`, "Payer Mix", { merge: mergeHeaderCells });
    }

    function ExpandAllAggregateGroupItems(): IGroup[] {
        let reportDataGroups = Aggregate(_.first(SelectedReportLevels.value) ?? IterateeTypes.finClassId);
        for (const rootLevelGroup of reportDataGroups)
        {
            ExpandAggregateGroupItems(rootLevelGroup)
        }
        return reportDataGroups;
    }

    // Drill into all aggregate items
    function ExpandAggregateGroupItems(group: IGroup) {
        group.ShowChild = true;
        const levelToGet = group.Level + 1;
        if (levelToGet >= SelectedReportLevels.value.length)
            return;

        const childern = Aggregate(SelectedReportLevels.value[levelToGet], group);
        if (!childern)
            return;
        
        group.ChildGroups = childern;

        for(const childGroup of group.ChildGroups)
            ExpandAggregateGroupItems(childGroup);
    }

    function ExpandAllRows(expandAll: boolean = true)
    {
        if (expandAll)
        {
            aggregatedData.value = ExpandAllAggregateGroupItems();
        }
        else
        {
            // Remove the child items
            const localAggregatedData = aggregatedData.value;
            localAggregatedData.forEach(o => {
                o.ShowChild = false;
                o.ChildGroups = undefined;
            });
            aggregatedData.value = localAggregatedData;
        }
    }

    const reportDateTypeName = computed(() =>{
        return PayerMixDateTypes.find(o => o.dateType == queryValues.value.dateType)?.name;
    });

    const reportInsuranceTypeName = computed(() => {
        return PayerMixInsuranceTypes.find(o => o.insuranceType == queryValues.value.insuranceType)?.name;
    })

</script>

<style scoped>
    @media print {
        .hideDuringPrint { display:none; }
    }
</style>

<template>

    <FoldingPanel :panel-open="queryDataPanelOpen" class="hideDuringPrint">
        <template #title>
            Report Options
        </template>
        <template #body>
            <PayerMixQueryOptions v-model:values="queryValues" @update:values="rawReportData = undefined">
                <template #bottomRow>
                    <LoadingSection :is-loading="loadingApiData" loading-msg="Loading report data...">
                        <DropDownSplitButton @onButtonClick="RunQuery()">
                            <template v-slot:button>
                                Run Report
                            </template>
                            <DropDownButtonItem @click="RunQueryForLastMonth">Last Month</DropDownButtonItem>
                            <DropDownButtonItem @click="RunQueryForLastQuarter">Last Quarter</DropDownButtonItem>
                            <DropDownButtonDivider/>
                            <DropDownButtonItem @click="RunQueryForCurrentMonth">Current Month</DropDownButtonItem>
                        </DropDownSplitButton>
                    </LoadingSection>
                </template>            
            </PayerMixQueryOptions>
        </template>
    </FoldingPanel>

    <template v-if="undefined !== rawReportData">
        <LoadingSection :is-loading="(loadingDataMessage?.length ?? 0) > 0" :loading-msg="loadingDataMessage">
            <div class="row hideDuringPrint">
                <div class="col-sm-8">
                    <FoldingFieldset title="Filters">
                        <OptionsDropPanel>
                            <template #title>Financial Class</template>
                            <template #options>
                                <ChoiceTable 
                                    :columns="financialClasspayeeTableColumns" 
                                    :data="FinancialClassPickerInfoWithPayees" />
                            </template>
                            <template #summary>
                                <MessageBadge>
                                    <SelectionCount :selCount="SelectedFinancialClassesWithPayeeIDs.length" :totalCount="FinancialClassPickerInfoWithPayees.length"/>
                                </MessageBadge>
                            </template>
                        </OptionsDropPanel>

                        <OptionsDropPanel>
                            <template #title>Payer</template>
                            <template #options>
                                <ChoiceTable :columns="payeeTableColumns" :data="PayerPickerInfoFilterOptions" :show-filters="true" />
                            </template>
                            <template #summary>
                                <MessageBadge>
                                    <SelectionCount :selCount="SelectedPayeeIDs.length" :totalCount="PayerPickerInfoFilterOptions.length"/>
                                </MessageBadge>
                            </template>                        
                        </OptionsDropPanel>

                        <OptionsDropPanel>
                            <template #title>Provider</template>
                            <template #options>
                                <ChoiceTable 
                                    :columns="renderingProviderTableColumns" 
                                    :data="RenderingProvidersFilterOptions" 
                                    :show-filters="true" />
                            </template>
                            <template #summary>
                                <MessageBadge>
                                    <SelectionCount :selCount="SelectedProviderIDs.length" :totalCount="RenderingProvidersFilterOptions.length"/>
                                </MessageBadge>
                            </template>
                        </OptionsDropPanel>

                        <OptionsDropPanel>
                            <template #title>Service Location</template>
                            <template #options>
                                <ChoiceTable
                                    :columns="serviceLocationTableColumns"
                                    :data="ServiceLocationFilterOptions"
                                    :show-filters="true" />
                            </template>
                            <template #summary>
                                <MessageBadge>
                                    <SelectionCount :selCount="SelectedLocationIDs.length" :totalCount="ServiceLocationFilterOptions.length"/>
                                </MessageBadge>
                            </template>
                        </OptionsDropPanel>

                        <template v-if="queryValues.includeProcedureCode">
                            <OptionsDropPanel v-if="ProcedureCodesFilterOptions.length">
                                <template #title>Procedure Codes</template>
                                <template #options>
                                    <ChoiceTable 
                                        :columns="procedureCodesTableColumns"
                                        :data="ProcedureCodesFilterOptions"
                                        :show-filters="true" />
                                </template>
                                <template #summary>
                                    <MessageBadge>
                                        <SelectionCount :selCount="SelectedProcedureIDs.length" :totalCount="ProcedureCodesFilterOptions.length"/>
                                    </MessageBadge>
                                </template>                    
                            </OptionsDropPanel>

                            <span class="text-muted" v-if="!ProcedureCodesFilterOptions.length">
                                No procedure codes are associated with the data
                            </span>
                        </template>
                    </FoldingFieldset>
                </div>
                <div class="col-sm-4">
                    <FoldingFieldset title="Group By">
                        <SelectAndOrderBy 
                            v-model:items="VisibleReportLevelOptions" 
                            @update:items="(items) => { ReportLevelOptions = items; InitializedReportTableData(); }"
                            @changed-selection="InitializedReportTableData()" />
                    </FoldingFieldset>
                </div>    
            </div>

            <div>
                <strong class="me-1">Date Range:</strong> 
                <PrettyDate :date="queryValues.startDate" :showTime="false"/> to <PrettyDate :date="queryValues.endDate" :showTime="false"/>,

                <strong class="me-1">Date Type:</strong>
                <span>
                    {{reportDateTypeName}}
                </span>,

                <strong class="me-1">Insurance Type:</strong>
                <span>
                    {{reportInsuranceTypeName}}
                </span>
            </div>

            <ChoiceTable :columns="resultsColumns" 
                        :data="flattenedAggregatedReportData" 
                        :footerVals="ResultsFooterValues"
                        screenReaderCaption="Payer Mix Results Table">
                <template #cell="{col, row}">
                    <template v-if="col.ExtraData?.FoldingCell">
                        <span class="text-primary">
                            <ToggleIcon 
                                v-if="row.Level +1 < SelectedReportLevels.length"
                                :folded="row.ShowChild"
                                :folded-value="false"
                                @update:folded="(val) => ToggleRow(row)" 
                                :title="`Click to ${row.ShowChild ? 'hide' : 'show'} the child rows`" />
                        </span>
                    </template>
                    <template v-else>
                        {{FormattedValueStr(col, row, TableRowType.Body)}}
                    </template>
                </template>
            </ChoiceTable>

            <div class="row hideDuringPrint ">
                <div>
                    <div class="float-end">
                        <button @click="PrintReport()" class="btn btn-primary me-1">
                            <em class="fa-solid fa-print"></em>
                            Print
                        </button>
                        <button @click="ExportToExcel()" class="btn btn-primary me-1">
                            <em class="fa-solid fa-file-excel"></em>
                            Export To Excel
                        </button>
                        <button @click="ExpandAllRows()" class="btn btn-info me-1" title="Expand all the levels in the table">
                            <em class="fa-solid fa-angles-down"></em>
                            Expand All
                        </button>
                        <button @click="ExpandAllRows(false)" class="btn btn-info me-1" v-if="aggregatedData.find(o => o.ShowChild)" title="Collapse all the child levels">
                            <em class="fa-solid fa-angles-up"></em>
                            Collapse All
                        </button>
                    </div>
                </div>
            </div>

            <!-- Used during development.  Can be removed later -->
            <template v-if="showPerformanceData">
                <FoldingPanel class="hideDuringPrint">
                    <template #title>
                        Performance Data
                    </template>
                    <template #body>
                        <div>
                            Aggregate Method: <MessageBadge>{{ellapsedTimeAggregate}}ms</MessageBadge>
                        </div>
                        <div>
                            Options Data: <MessageBadge>{{ellapsedTimeOptions}}ms</MessageBadge>
                        </div>
                        <div>
                            Report Data API Call: <MessageBadge>{{ellapsedTimeReportDataApiCall}}ms</MessageBadge>
                        </div>
                    </template>
                </FoldingPanel>
            </template>
        </LoadingSection>
    </template>

</template>
